import React, { useEffect, useRef, useState, useCallback } from 'react';
import RunStatus from './RunStatus';
import RunHistory from './RunHistory';

const TERMINAL = ['completed', 'failed', 'timeout'];

const LOOP_LABELS = {
  starting: 'Starting…',
  running: 'Running',
  idle: 'Queued next run',
  stopped: 'Stopped',
  error: 'Error'
};

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function GccRunner({ api, pollIntervalMs }) {
  const [farms, setFarms] = useState([]);
  const [farm, setFarm] = useState('');
  const [integration, setIntegration] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [scenarioId, setScenarioId] = useState('');
  const [activeRun, setActiveRun] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState('');
  const [loopRunning, setLoopRunning] = useState(false);
  const [loopStatuses, setLoopStatuses] = useState({});
  const pollRef = useRef(null);
  const loopControlRef = useRef({ stopped: true });

  useEffect(() => {
    api.listFarms()
      .then((res) => {
        const available = res.farms || [];
        setFarms(available);
        if (available.length) setFarm(available[0].code);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [api]);

  const loadHistory = useCallback((code) => {
    if (!code) return;
    api.listRuns(code).then((res) => setHistory(res.runs || [])).catch((e) => setError(e.message));
  }, [api]);

  // Stop any in-progress continuous loops on unmount.
  useEffect(() => () => { loopControlRef.current.stopped = true; }, []);

  useEffect(() => {
    if (!farm) return;
    // Switching farms invalidates any continuous run in progress for the previous farm.
    loopControlRef.current.stopped = true;
    setLoopRunning(false);
    setLoopStatuses({});
    setLoadingScenarios(true);
    setError('');
    setActiveRun(null);
    setSelectedRun(null);
    api.listGccScenarios(farm)
      .then((res) => {
        setIntegration(res.integration || null);
        const discovered = res.scenarios || [];
        setScenarios(discovered);
        const firstActive = discovered.find((item) => item.active);
        setScenarioId(firstActive ? firstActive.id : '');
        loadHistory(farm);
      })
      .catch((e) => {
        setIntegration(null);
        setScenarios([]);
        setScenarioId('');
        setError(e.message);
      })
      .finally(() => setLoadingScenarios(false));
  }, [api, farm, loadHistory]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const startPolling = useCallback((code, runId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const run = await api.getRun(code, runId);
        setActiveRun(run);
        if (TERMINAL.includes((run.Status || '').toLowerCase())) {
          stopPolling();
          loadHistory(code);
        }
      } catch (e) {
        setError(e.message);
        stopPolling();
      }
    }, pollIntervalMs);
  }, [api, loadHistory, pollIntervalMs, stopPolling]);

  const selectedScenario = scenarios.find((item) => item.id === scenarioId);
  const running = activeRun && (activeRun.Status || '').toLowerCase() === 'running';

  // Runs one scenario to completion, then immediately restarts it until the loop is stopped.
  const runScenarioLoop = useCallback(async (farmCode, integrationId, scenario) => {
    const control = loopControlRef.current;
    for (;;) {
      setLoopStatuses((prev) => ({
        ...prev,
        [scenario.id]: {
          ...(prev[scenario.id] || { iteration: 0, passCount: 0, failCount: 0 }),
          status: 'starting',
          error: null
        }
      }));

      let res;
      try {
        res = await api.triggerRun(farmCode, {
          integrationId,
          scenarioId: scenario.id,
          scenarioName: scenario.name
        });
      } catch (e) {
        setLoopStatuses((prev) => ({
          ...prev,
          [scenario.id]: { ...(prev[scenario.id] || {}), status: 'error', error: e.message }
        }));
        return;
      }

      setLoopStatuses((prev) => ({
        ...prev,
        [scenario.id]: { ...(prev[scenario.id] || {}), status: 'running', runId: res.runId }
      }));

      let finalRun = null;
      while (!finalRun) {
        await sleep(pollIntervalMs);
        try {
          const run = await api.getRun(farmCode, res.runId);
          if (TERMINAL.includes((run.Status || '').toLowerCase())) {
            finalRun = run;
          }
        } catch (e) {
          setLoopStatuses((prev) => ({
            ...prev,
            [scenario.id]: { ...(prev[scenario.id] || {}), status: 'error', error: e.message }
          }));
          return;
        }
      }

      const passed = (finalRun.Status || '').toLowerCase() === 'completed';
      setLoopStatuses((prev) => {
        const existing = prev[scenario.id] || { iteration: 0, passCount: 0, failCount: 0 };
        return {
          ...prev,
          [scenario.id]: {
            ...existing,
            status: control.stopped ? 'stopped' : 'idle',
            iteration: existing.iteration + 1,
            passCount: existing.passCount + (passed ? 1 : 0),
            failCount: existing.failCount + (passed ? 0 : 1),
            lastRun: finalRun
          }
        };
      });

      loadHistory(farmCode);

      if (control.stopped) return;
    }
  }, [api, pollIntervalMs, loadHistory]);

  const startAllScenarios = () => {
    if (!integration) return;
    const activeScenarios = scenarios.filter((item) => item.active);
    if (!activeScenarios.length) return;

    loopControlRef.current = { stopped: false };
    setLoopStatuses(
      activeScenarios.reduce((acc, item) => {
        acc[item.id] = { status: 'starting', iteration: 0, passCount: 0, failCount: 0 };
        return acc;
      }, {})
    );
    setLoopRunning(true);
    activeScenarios.forEach((scenario) => runScenarioLoop(farm, integration.id, scenario));
  };

  const stopAllScenarios = () => {
    loopControlRef.current.stopped = true;
    setLoopRunning(false);
  };

  const loopValues = Object.values(loopStatuses);
  const anyLoopActive = loopValues.some((item) => item.status === 'starting' || item.status === 'running');
  const continuousActive = loopRunning || anyLoopActive;
  const totalIterations = loopValues.reduce((sum, item) => sum + (item.iteration || 0), 0);
  const totalPass = loopValues.reduce((sum, item) => sum + (item.passCount || 0), 0);
  const totalFail = loopValues.reduce((sum, item) => sum + (item.failCount || 0), 0);

  const trigger = async () => {
    if (!selectedScenario || !integration) return;
    setError('');
    setTriggering(true);
    try {
      const res = await api.triggerRun(farm, {
        integrationId: integration.id,
        scenarioId: selectedScenario.id,
        scenarioName: selectedScenario.name
      });
      const run = {
        Farm: res.farm,
        RunId: res.runId,
        Status: res.status,
        StartedAt: res.startedAt,
        ScenarioName: selectedScenario.name
      };
      setActiveRun(run);
      startPolling(farm, res.runId);
      loadHistory(farm);
    } catch (e) {
      setError(e.message);
    } finally {
      setTriggering(false);
    }
  };

  if (loading) return <div className="card">Loading GCC tests…</div>;
  if (!farms.length) return <div className="card">No farms with stored credentials are available.</div>;

  return (
    <div className="runner gcc-runner">
      <div className="gcc-heading">
        <div>
          <p className="eyebrow">Separate integration workspace</p>
          <h2>GCC SMAX Tests</h2>
          <p className="muted">Discover and run individual GCC scenarios without changing the existing SMAX test suite.</p>
        </div>
      </div>

      <div className="card controls gcc-controls">
        <label htmlFor="gcc-farm-select">Farm</label>
        <select id="gcc-farm-select" value={farm} onChange={(e) => setFarm(e.target.value)} disabled={running || continuousActive}>
          {farms.map((item) => <option key={item.code} value={item.code}>{item.code}</option>)}
        </select>
        <label htmlFor="gcc-scenario-select">Scenario</label>
        <select id="gcc-scenario-select" value={scenarioId} onChange={(e) => setScenarioId(e.target.value)} disabled={running || loadingScenarios || continuousActive}>
          {scenarios.filter((item) => item.active).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <button className="primary" onClick={trigger} disabled={triggering || running || !selectedScenario || loadingScenarios || continuousActive}>
          {running ? 'Running…' : triggering ? 'Starting…' : 'Run scenario'}
        </button>
      </div>

      {error && <div className="error card">{error}</div>}
      {integration && <div className="integration-note">Integration: <strong>{integration.name}</strong> · {scenarios.filter((item) => item.active).length} active scenarios</div>}

      {integration && (
        <div className="card continuous-panel">
          <div className="continuous-header">
            <div>
              <h3>Continuous execution</h3>
              <p className="muted">Runs every active GCC scenario in parallel; each one restarts automatically as soon as it finishes, until you stop it.</p>
            </div>
            {loopRunning ? (
              <button className="danger" onClick={stopAllScenarios}>Stop all</button>
            ) : (
              <button
                className="primary"
                onClick={startAllScenarios}
                disabled={anyLoopActive || !scenarios.some((item) => item.active)}
              >
                Run all scenarios continuously
              </button>
            )}
          </div>
          {(loopRunning || anyLoopActive) && (
            <div className="continuous-summary">
              <span className="test-metric"><strong>{totalIterations}</strong> total runs</span>
              <span className="test-metric ok-text"><strong>{totalPass}</strong> passed</span>
              {totalFail > 0 && (
                <span className="test-failures"><strong>{totalFail}</strong> failed</span>
              )}
              {!loopRunning && anyLoopActive && <span className="muted">Stopping — finishing in-flight runs…</span>}
            </div>
          )}
        </div>
      )}

      {scenarios.length > 0 && (
        <div className="card scenario-catalog">
          <div className="catalog-header"><h3>GCC scenario catalog</h3><span className="muted">{scenarios.length} discovered</span></div>
          <div className="scenario-list">
            {scenarios.map((item) => {
              const loop = loopStatuses[item.id];
              return (
                <button
                  key={item.id}
                  className={`scenario-item ${item.id === scenarioId ? 'selected' : ''} ${!item.active ? 'inactive' : ''} ${loop ? `loop-${loop.status}` : ''}`}
                  onClick={() => item.active && !continuousActive && setScenarioId(item.id)}
                  disabled={!item.active || running || continuousActive}
                >
                  <span className="scenario-item-name">{item.name}</span>
                  <span className="scenario-item-meta">
                    {loop ? (
                      <>
                        <span className={`loop-badge loop-${loop.status}`}>{LOOP_LABELS[loop.status] || loop.status}</span>
                        <span className="loop-counts">{loop.iteration} runs · {loop.passCount} passed · {loop.failCount} failed</span>
                      </>
                    ) : (
                      <span className="scenario-state">{item.active ? 'Active' : 'Inactive'}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {activeRun && <RunStatus run={activeRun} />}
      {!activeRun && selectedRun && <RunStatus run={selectedRun} />}
      <RunHistory
        runs={history.filter((run) => run.IntegrationId === integration?.id)}
        selectedRunId={selectedRun?.RunId}
        onSelectRun={setSelectedRun}
        showScenario
      />
    </div>
  );
}